import React, { useState, useCallback, useContext } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Box, Alert } from '@mui/material';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { getStudentsByClassroom, getSubjects } from '../../api/sponteAPI';
import { AuthContext } from '../../contexts/Auth/AuthContext';
import { getGradesByStudent, getFinalResults, formatNotaParaExibicao } from '../../api/autenticoAPI';
import { jsPDF } from 'jspdf';
// eslint-disable-next-line no-unused-vars
import html2canvas from 'html2canvas';
import logo from '../ModalResultado/logo.png';
import { getUnidadeData } from '../../utils/YEARCONSTS';
import { determineStatus, calculateMediaFinalAfterExam } from '../ModalResultado/utils';
import { logDebug } from '../../utils/logger';
import { normalizeStudentsByClassroom, normalizeSubjects } from '../../utils/sponteDataNormalizer';

const BoletimCombinadoPDF = ({ turmaId, turmaNome }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [students, setStudents] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [combinedPDF, setCombinedPDF] = useState(null);
  const auth = useContext(AuthContext);
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setProgress(0);
      setCurrentStudentIndex(-1);
      setProcessedCount(0);
      setTotalCount(0);
    }
  };

  // Função mais eficiente para carregar os dados dos alunos e gerar o PDF
  const handleExportAllReports = useCallback(async () => {
    try {
      // Verificar se existe turma selecionada
      if (!turmaId) {
        setError('Selecione uma turma primeiro');
        return;
      }

      setLoading(true);
      setError(null);
      setProgress(0);
      setProcessedCount(0);
      
      // 1. Carregar lista de alunos
      const response = await getStudentsByClassroom(turmaId);
      const studentsList = normalizeStudentsByClassroom(response);
      
      if (studentsList.length === 0) {
        throw new Error('Nenhum aluno encontrado nesta turma');
      }
      
      // Ordenar os alunos por número de chamada
      const sortedStudents = [...studentsList].sort((a, b) => {
        const numA = parseInt(a.NumeroChamada) || 0;
        const numB = parseInt(b.NumeroChamada) || 0;
        return numA - numB;
      });
      
      setStudents(sortedStudents);
      setTotalCount(sortedStudents.length);
      
      // 2. Inicializar o PDF
      const pdf = new jsPDF({
        format: "a4",
        unit: "mm",
        orientation: "portrait",
      });
      
      // 3. Carregar dados das disciplinas uma única vez (comum para todos os alunos)
      const subjectsData = await getSubjects(turmaId);
      logDebug('Dados de disciplinas carregados:', subjectsData);
      
      const subjects = normalizeSubjects(subjectsData);
      
      logDebug('Lista de disciplinas processada:', subjects);
      
      // 4. Processar cada aluno e adicionar ao PDF
      for (let i = 0; i < sortedStudents.length; i++) {
        // Atualizar progresso
        setCurrentStudentIndex(i);
        setProgress(Math.round(((i + 1) / sortedStudents.length) * 100));
        
        const student = sortedStudents[i];
        const studentName = `${student.NumeroChamada} - (${student.AlunoID}) ${student.Nome}`;
        
        // Carregar dados do aluno
        try {
          // Carregar notas e resultados finais
          const [grades, finalResults] = await Promise.all([
            getGradesByStudent(student.AlunoID, turmaId),
            getFinalResults(student.AlunoID, turmaId),
          ]);
          
          logDebug(`Notas do aluno ${student.Nome} (ID ${student.AlunoID}):`, grades);
          logDebug(`Resultados finais do aluno ${student.Nome}:`, finalResults);
          
          // Renderizar boletim direto para o PDF
          if (i > 0) {
            pdf.addPage();
          }
          
          // Adicionar conteúdo do boletim diretamente ao PDF
          await addBoletimToPDF(pdf, {
            student: {
              nome: studentName,
              AlunoID: student.AlunoID,
              turma: turmaNome
            },
            subjects,
            grades,
            finalResults,
            unidade: auth.user?.unit
          });
          
          setProcessedCount(i + 1);
        } catch (error) {
          console.error(`Erro ao processar boletim do aluno ${studentName}:`, error);
          // Continuar com o próximo aluno
        }
      }
      
      // 5. Finalizar e exibir PDF
      const pdfBlob = pdf.output("bloburl");
      setCombinedPDF(pdfBlob);
      window.open(pdfBlob, "_blank");
      
      setLoading(false);
      setCurrentStudentIndex(-1);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setError('Erro ao gerar o PDF. Por favor, tente novamente: ' + error.message);
      setLoading(false);
      setCurrentStudentIndex(-1);
    }
  }, [turmaId, turmaNome, auth.user?.unit]);
  
  // Função para adicionar um boletim ao PDF
  const addBoletimToPDF = async (pdf, data) => {
    const { student, subjects, grades, finalResults, unidade } = data;
    
    // Logs de depuração
    logDebug('Unidade:', unidade);
    logDebug('Dados para boletim:', {
      nomeAluno: student.nome,
      idAluno: student.AlunoID,
      unidade: unidade,
      qtdeDisciplinas: subjects?.length || 0,
      contemNotas: grades != null,
      periodos: grades ? Object.keys(grades) : []
    });
    
    // Helper para garantir que todos os valores sejam strings válidas para pdf.text
    const safeText = (value) => {
      if (value === undefined || value === null) return "-";
      return String(value);
    };
    
    const getGradeForSubject = (disciplinaId, period, gradesData) => {
      if (!disciplinaId || !gradesData) return "-";
      
      const periodKey = `period${period}`;
      if (!gradesData[periodKey]) return "-";
      
      const gradeObj = gradesData[periodKey].find(
        g => String(g.DisciplinaID) === String(disciplinaId)
      );
      
      if (!gradeObj || !gradeObj.mb) return "-";
      
      // Verificar se há nota de recuperação melhor que MB
      let bestGrade = gradeObj.mb;
      let usedRecovery = false;
      
      // Converter para números para comparação
      const mbValue = parseFloat(String(gradeObj.mb).replace(',', '.'));
      const rmValue = gradeObj.rm ? parseFloat(String(gradeObj.rm).replace(',', '.')) : 0;
      const rbValue = gradeObj.rb ? parseFloat(String(gradeObj.rb).replace(',', '.')) : 0;
      
      // Usar o maior valor entre as três notas
      if (rmValue > mbValue && rmValue > 0) {
        bestGrade = gradeObj.rm;
        usedRecovery = true;
      }
      
      if (rbValue > mbValue && rbValue > rmValue && rbValue > 0) {
        bestGrade = gradeObj.rb;
        usedRecovery = true;
      }
      
      // Usar a função padronizada de formatação que garante vírgula como separador
      // e adiciona o indicador de recuperação quando necessário
      return formatNotaParaExibicao(bestGrade, usedRecovery);
    };
    
    const calculateFinalAverage = (disciplinaId, gradesData) => {
      if (!disciplinaId) return "-";
      
      // Recolher todas as notas bimestrais
      const gradesByPeriod = [1, 2, 3, 4].map(period => {
        const periodKey = `period${period}`;
        if (!gradesData[periodKey]) return null;
        
        const gradeObj = gradesData[periodKey].find(
          g => String(g.DisciplinaID) === String(disciplinaId)
        );
        
        if (!gradeObj || !gradeObj.mb) return null;
        
        // Verificar se há nota de recuperação (RM ou RB) melhor que MB
        let bestGrade = gradeObj.mb;
        let usedRecovery = false;
        
        // Converter para números para comparação
        const mbValue = parseFloat(String(gradeObj.mb).replace(',', '.'));
        const rmValue = gradeObj.rm ? parseFloat(String(gradeObj.rm).replace(',', '.')) : 0;
        const rbValue = gradeObj.rb ? parseFloat(String(gradeObj.rb).replace(',', '.')) : 0;
        
        // Usar o maior valor entre as três notas
        if (rmValue > mbValue && rmValue > 0) {
          bestGrade = gradeObj.rm;
          usedRecovery = true;
        }
        
        if (rbValue > mbValue && rbValue > rmValue && rbValue > 0) {
          bestGrade = gradeObj.rb;
          usedRecovery = true;
        }
        
        // Garantir que estamos trabalhando com um valor numérico para cálculos
        // Converter para número antes de retornar
        const valueForCalculation = parseFloat(String(bestGrade).replace(',', '.'));
        
        return { 
          value: valueForCalculation,
          usedRecovery
        };
      });
      
      // Verificar se temos todas as 4 notas
      const validGrades = gradesByPeriod.filter(g => g !== null);
      
      if (validGrades.length !== 4) {
        return "-";
      }
      
      // Calcular a média
      const sum = validGrades.reduce((acc, grade) => acc + grade.value, 0);
      const average = (sum / 4).toFixed(1);
      
      // Verificar se alguma nota veio de recuperação
      const usedRecoveryInAnyPeriod = validGrades.some(grade => grade.usedRecovery);
      
      // Usar a função padronizada para formatar o resultado final
      return formatNotaParaExibicao(average, usedRecoveryInAnyPeriod);
    };
    
    // Obter dados da unidade
    const unidadeData = getUnidadeData(unidade);
    logDebug('Dados da unidade recuperados:', unidadeData);
    
    // Garantir que todas as propriedades necessárias existam
    const dadosUnidade = {
      nome: unidadeData.nome || `Colégio Autêntico - ${unidade || "Campina Grande"}`,
      endereco: unidadeData.endereco || "Rua Severino Verônica, 117 - Conceição - CEP 58.401-314",
      website: unidadeData.website || "www.colegioautentico.com",
      telefone: unidadeData.telefone || "(83) 3342-0044",
      anoLetivo: unidadeData.anoLetivo || "2025",
    };
    
    // Extrair informações do resultado final
    const exameFinal = {};
    
    if (finalResults?.disciplinas) {
      finalResults.disciplinas.forEach(discipline => {
        const id = String(discipline.DisciplinaID);
        if (discipline.exameFinal && discipline.exameFinal !== "0") {
          exameFinal[id] = discipline.exameFinal;
        }
      });
    }
    
    // Calcular situação atual para todas as disciplinas baseado nos dados reais
    // Isso garante que a situação exibida seja sempre a atual, não apenas a salva
    const situacao = {};
    subjects.forEach(subject => {
      const id = String(subject.DisciplinaID);
      
      // Média anual (M.A.) — mesma regra do ModalResultado / app
      const mediaAnualStr = calculateFinalAverage(subject.DisciplinaID, grades);
      
      if (!mediaAnualStr || mediaAnualStr === "-") {
        situacao[id] = "CURSANDO";
        return;
      }
      
      const mediaAnualLimpa = mediaAnualStr.replace("*", "").trim();
      const exameFinalValue = exameFinal[id] || "";
      
      // Calcular situação atual usando a função determineStatus
      // Isso vai retornar APROVADO, REPROVADO, EM EXAME ou CURSANDO baseado nos dados reais
      situacao[id] = determineStatus(mediaAnualLimpa, exameFinalValue);
    });
    
    // Posições e tamanhos
    const pageWidth = 210;
    const marginLeft = 15;
    const contentWidth = pageWidth - 2 * marginLeft;
    let y = 20; // Posição vertical atual
    
    // Adicionar cabeçalho
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, 297, 'F');
    
    // Logo e informações da escola
    try {
      const logoBase64 = logo;
      pdf.addImage(logoBase64, 'PNG', marginLeft, y, 25, 25);
    } catch (e) {
      console.error("Erro ao adicionar logo:", e);
    }
    
    // Informações da escola
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(0, 32, 96); // Azul escuro
    pdf.text(safeText(dadosUnidade.nome), marginLeft + 35, y + 9);
    
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0); // Preto para o endereço
    pdf.text(safeText(dadosUnidade.endereco), marginLeft + 35, y + 14);
    
    pdf.setFontSize(8);
    pdf.text(`${safeText(dadosUnidade.website)} • ${safeText(dadosUnidade.telefone)}`, marginLeft + 35, y + 19);
    
    y += 33; // Espaço para o título do boletim
    
    // Título "BOLETIM ESCOLAR"
    pdf.setFillColor(21, 42, 129); // Azul escuro como na imagem
    pdf.rect(marginLeft, y, contentWidth, 10, 'F');
    
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255); // Branco
    pdf.text("BOLETIM ESCOLAR", pageWidth / 2, y + 6, { align: "center" });
    
    y += 15;
    
    // Informações do aluno
    pdf.setFillColor(248, 249, 250); // Cor cinza claro #f8f9fa
    pdf.rect(marginLeft, y, contentWidth, 12, 'F');
    
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    // Formatar o nome do aluno para mostrar apenas o nome, sem ID
    const alunoNomeLimpo = student.nome.split('-')[1]?.trim().replace(/\s*\([^)]*\)\s*/g, '') || student.nome.replace(/\s*\([^)]*\)\s*/g, '');
    
    // Ajustando altura do retângulo para comportar o nome da turma
    pdf.rect(marginLeft, y, contentWidth, 18, 'F');
    
    // Nome do aluno
    pdf.text(`Aluno: ${alunoNomeLimpo}`, marginLeft + 5, y + 7);
    
    // Turma do aluno
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(`Turma: ${safeText(student.turma)}`, marginLeft + 5, y + 14);
    
    // Ano letivo (no canto direito)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Ano Letivo:", pageWidth - marginLeft - 40, y + 7);
    
    pdf.setFont("helvetica", "normal");
    pdf.text(safeText(dadosUnidade.anoLetivo), pageWidth - marginLeft - 15, y + 7);
    
    // Aumentando o valor de y para compensar a altura adicional
    y += 23; // Alterado de 17 para 23
    
    // Tabela de notas
    // Cabeçalho da tabela
    pdf.setFillColor(248, 249, 250);
    pdf.rect(marginLeft, y, contentWidth, 10, 'F');
    
    pdf.setDrawColor(224, 224, 224); // Cor #e0e0e0 para bordas
    pdf.setLineWidth(0.2);
    
    // Linhas horizontais do cabeçalho
    pdf.line(marginLeft, y, marginLeft + contentWidth, y);
    pdf.line(marginLeft, y + 10, marginLeft + contentWidth, y + 10);
    
    // Linhas verticais: Disciplina + 4 bimestres + MA + EF + MF + Situação
    const colCount = 9;
    const colWidths = [
      contentWidth * 0.26, // Disciplina
      contentWidth * 0.085, // 1º Bim
      contentWidth * 0.085, // 2º Bim
      contentWidth * 0.085, // 3º Bim
      contentWidth * 0.085, // 4º Bim
      contentWidth * 0.09, // MA
      contentWidth * 0.082, // EF
      contentWidth * 0.093, // MF (após exame)
      contentWidth * 0.135, // Situação
    ];
    
    let xPos = marginLeft;
    for (let i = 0; i <= colCount; i++) {
      pdf.line(xPos, y, xPos, y + 10);
      if (i < colCount) xPos += colWidths[i];
    }
    
    // Textos do cabeçalho
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(26, 35, 126);
    
    xPos = marginLeft;
    pdf.text("Disciplinas", xPos + 3, y + 6);
    xPos += colWidths[0];
    
    for (let i = 1; i <= 4; i++) {
      pdf.text(`${i}º Bim`, xPos + colWidths[i]/2, y + 6, { align: "center" });
      xPos += colWidths[i];
    }
    
    pdf.text("MA", xPos + colWidths[5]/2, y + 6, { align: "center" });
    xPos += colWidths[5];
    
    pdf.text("EF", xPos + colWidths[6]/2, y + 6, { align: "center" });
    xPos += colWidths[6];
    
    pdf.text("MF", xPos + colWidths[7]/2, y + 6, { align: "center" });
    xPos += colWidths[7];
    
    pdf.text("Situação", xPos + colWidths[8]/2, y + 6, { align: "center" });
    
    y += 10;
    
    // Dados das disciplinas
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    
    // Para cada disciplina
    for (let i = 0; i < subjects.length; i++) {
      const subject = subjects[i];
      const rowHeight = 8;
      
      logDebug(`Processando disciplina: ${subject.Nome} (ID: ${subject.DisciplinaID})`);
      
      // Fundo alternado
      if (i % 2 === 0) {
        pdf.setFillColor(255, 255, 255); // Branco
      } else {
        pdf.setFillColor(249, 250, 251); // Cinza muito claro #f9fafb
      }
      pdf.rect(marginLeft, y, contentWidth, rowHeight, 'F');
      
      // Bordas
      pdf.line(marginLeft, y + rowHeight, marginLeft + contentWidth, y + rowHeight);
      
      xPos = marginLeft;
      for (let j = 0; j <= colCount; j++) {
        pdf.line(xPos, y, xPos, y + rowHeight);
        if (j < colCount) xPos += colWidths[j];
      }
      
      // Valores
      xPos = marginLeft;
      pdf.setFont("helvetica", "normal");
      
      // Nome da disciplina
      pdf.text(safeText(subject.Nome || "-"), xPos + 3, y + rowHeight/2 + 2);
      xPos += colWidths[0];
      
      // Notas dos bimestres
      for (let bim = 1; bim <= 4; bim++) {
        const nota = getGradeForSubject(subject.DisciplinaID, bim, grades);
        pdf.text(safeText(nota), xPos + colWidths[bim]/2, y + rowHeight/2 + 2, { align: "center" });
        xPos += colWidths[bim];
      }
      
      const mediaAnual = calculateFinalAverage(subject.DisciplinaID, grades);
      pdf.setFont("helvetica", "bold");
      pdf.text(safeText(mediaAnual), xPos + colWidths[5]/2, y + rowHeight/2 + 2, { align: "center" });
      xPos += colWidths[5];
      
      pdf.setFont("helvetica", "normal");
      const efVal = exameFinal[subject.DisciplinaID] || "";
      pdf.text(safeText(efVal || "-"), xPos + colWidths[6]/2, y + rowHeight/2 + 2, { align: "center" });
      xPos += colWidths[6];
      
      const mediaAposExame = calculateMediaFinalAfterExam(mediaAnual, efVal);
      pdf.setFont("helvetica", "bold");
      pdf.text(safeText(mediaAposExame), xPos + colWidths[7]/2, y + rowHeight/2 + 2, { align: "center" });
      xPos += colWidths[7];
      
      const status = situacao[subject.DisciplinaID] || "CURSANDO";
      
      // Cores para a situação
      if (status === "APROVADO") {
        pdf.setTextColor(46, 125, 50); // Verde #2e7d32
      } else if (status === "EM EXAME") {
        pdf.setTextColor(237, 108, 2); // Laranja #ed6c02
      } else {
        pdf.setTextColor(102, 102, 102); // Cinza #666
      }
      
      pdf.text(safeText(status), xPos + colWidths[8]/2, y + rowHeight/2 + 2, { align: "center" });
      pdf.setTextColor(0, 0, 0); // Resetar cor
      
      y += rowHeight;
    }
    
    // Legenda
    pdf.line(marginLeft, y - 5, marginLeft + contentWidth, y - 5);
    y += 15;
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    
    // Centralizar as legendas distribuídas uniformemente
    const legendas = [
      "MA - Média Anual",
      "MF - Média Final (após exame; sem EF, igual à MA)",
      "EF - Exame Final",
      "* - Nota após recuperação",
    ];
    
    const espacamentoEntreLegendas = contentWidth / (legendas.length + 1);
    legendas.forEach((legenda, index) => {
      const xPos = marginLeft + espacamentoEntreLegendas * (index + 1);
      pdf.text(legenda, xPos, y, { align: "center" });
    });
    
    // Assinaturas
    y += 15;
    
    // Linha de assinaturas
    const assinaturaWidth = contentWidth / 2 - 10;
    
    pdf.line(marginLeft + 10, y, marginLeft + 10 + assinaturaWidth - 20, y);
    pdf.line(marginLeft + contentWidth - 10 - assinaturaWidth + 20, y, marginLeft + contentWidth - 10, y);
    
    y += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text("Assinatura do Responsável", marginLeft + assinaturaWidth/2 - 10, y, { align: "center" });
    pdf.text("Assinatura do Diretor(a)", marginLeft + contentWidth - assinaturaWidth/2 - 10, y, { align: "center" });
    
    return pdf;
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<LibraryBooksIcon style={{ fontSize: '1.25rem' }} />}
        onClick={handleOpen}
        disabled={!turmaId}
        sx={{ 
          height: '40px',
          textTransform: 'none',
          borderRadius: '4px',
          fontSize: '0.9rem',
          whiteSpace: 'nowrap'
        }}
      >
        Imprimir Boletins da Turma
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="export-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="export-dialog-title">
          Imprimir Boletins - {turmaNome}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && currentStudentIndex === -1 && processedCount === 0 ? (
            <Typography>
              Esta operação irá gerar um único arquivo PDF contendo os boletins de todos os 
              alunos da turma {turmaNome}.
              <br/><br/>
              O processo pode demorar alguns minutos dependendo do número de alunos.
              <br/><br/>
              Após a conclusão, o PDF será aberto automaticamente em uma nova aba do navegador.
            </Typography>
          ) : loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress variant="determinate" value={progress} size={60} sx={{ mb: 2 }} />
              <Typography variant="body1">
                Gerando boletins... {progress}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processando aluno {processedCount + 1} de {totalCount}
              </Typography>
              {currentStudentIndex >= 0 && currentStudentIndex < students.length && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Aluno atual: {students[currentStudentIndex].Nome}
                </Typography>
              )}
            </Box>
          ) : combinedPDF ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                PDF combinado gerado com sucesso!
              </Alert>
              <Typography variant="body2" color="text.secondary">
                O PDF foi aberto em uma nova aba do navegador. Você também pode baixá-lo novamente:
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => window.open(combinedPDF, "_blank")}
              >
                Abrir PDF
              </Button>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Fechar
          </Button>
          {!loading && currentStudentIndex === -1 && processedCount === 0 && (
            <Button 
              onClick={handleExportAllReports}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Iniciar Geração do PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BoletimCombinadoPDF; 